import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Users, MapPin, X, UserPlus, UserMinus, Shield, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GroupManagement({ token }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupAdmins, setGroupAdmins] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [editGroupName, setEditGroupName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      toast.error('Qrupları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMembers(response.data);
    } catch (error) {
      toast.error('Üzvləri yükləmək mümkün olmadı');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error('Qrup adı daxil edin');
      return;
    }
    try {
      await axios.post(`${API}/groups`, { name: newGroupName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Qrup yaradıldı');
      setShowCreateDialog(false);
      setNewGroupName('');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Qrup yaratmaq mümkün olmadı');
    }
  };

  const handleEditGroup = async (e) => {
    e.preventDefault();
    if (!editGroupName.trim() || !selectedGroup) return;
    try {
      await axios.put(`${API}/groups/${selectedGroup.id}`, { name: editGroupName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Qrup yeniləndi');
      setShowEditDialog(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Qrupu yeniləmək mümkün olmadı');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Bu qrupu silmək istədiyinizə əminsiniz?')) return;
    try {
      await axios.delete(`${API}/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Qrup silindi');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Qrupu silmək mümkün olmadı');
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !selectedGroup) return;
    try {
      await axios.post(`${API}/groups/${selectedGroup.id}/members/${selectedUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Üzv əlavə edildi');
      setShowAddMemberDialog(false);
      setSelectedUserId('');
      fetchGroupMembers(selectedGroup.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Üzv əlavə etmək mümkün olmadı');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    try {
      await axios.delete(`${API}/groups/${selectedGroup.id}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Üzv silindi');
      fetchGroupMembers(selectedGroup.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Üzvü silmək mümkün olmadı');
    }
  };

  const openMembersDialog = (group) => {
    setSelectedGroup(group);
    fetchGroupMembers(group.id);
    setShowMembersDialog(true);
  };

  const openEditDialog = (group) => {
    setSelectedGroup(group);
    setEditGroupName(group.name);
    setShowEditDialog(true);
  };

  // Admin management functions
  const fetchGroupAdmins = async (groupId) => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupAdmins(response.data);
    } catch (error) {
      console.error('Admins fetch error:', error);
      setGroupAdmins([]);
    }
  };

  const openAdminDialog = (group) => {
    setSelectedGroup(group);
    fetchGroupAdmins(group.id);
    setShowAdminDialog(true);
  };

  const handleAssignAdmin = async () => {
    if (!selectedAdminId || !selectedGroup) return;
    try {
      await axios.post(`${API}/groups/${selectedGroup.id}/admins/${selectedAdminId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin təyin edildi');
      setSelectedAdminId('');
      fetchGroupAdmins(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin təyin etmək mümkün olmadı');
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    if (!selectedGroup) return;
    try {
      await axios.delete(`${API}/groups/${selectedGroup.id}/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin silindi');
      fetchGroupAdmins(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admini silmək mümkün olmadı');
    }
  };

  // Get admin users not assigned to current group
  const getAvailableAdmins = () => {
    if (!selectedGroup) return [];
    const currentAdminIds = groupAdmins.map(a => a.id);
    return users.filter(u => 
      u.role === 'admin' && 
      !currentAdminIds.includes(u.id)
    );
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      operator: 'Operator',
      technician: 'Texnik',
      accountant: 'Mühasib'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      operator: 'bg-blue-100 text-blue-800',
      technician: 'bg-green-100 text-green-800',
      accountant: 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Get users not in current group
  const getAvailableUsers = () => {
    if (!selectedGroup) return users;
    return users.filter(u => !u.groups?.includes(selectedGroup.id) && u.role !== 'admin');
  };

  if (loading) {
    return <div className="text-center py-8">Yüklənir...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Qruplar / Ərazilər</h2>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Qrup
        </Button>
      </div>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Heç bir qrup yoxdur</p>
          <p className="text-gray-400 text-sm">Yeni qrup yaratmaq üçün yuxarıdakı düyməni basın</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <div key={group.id} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-gray-800">{group.name}</h3>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(group)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteGroup(group.id)}
                    className="h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              
              {/* Admin Badge */}
              {group.admin_ids && group.admin_ids.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-purple-600 mb-2">
                  <Crown className="w-3 h-3" />
                  <span>{group.admin_ids.length} Admin</span>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openMembersDialog(group)}
                  className="flex-1"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Üzvlər
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAdminDialog(group)}
                  className="flex-1"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Adminlər
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Qrup Yarat</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGroup} className="space-y-4">
            <div>
              <Label htmlFor="groupName">Qrup Adı</Label>
              <Input
                id="groupName"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Məsələn: Nəsimi rayonu"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Ləğv et
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                Yarat
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Qrupu Redaktə Et</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditGroup} className="space-y-4">
            <div>
              <Label htmlFor="editGroupName">Qrup Adı</Label>
              <Input
                id="editGroupName"
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Ləğv et
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                Yadda saxla
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-600" />
              {selectedGroup?.name} - Üzvlər
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add Member Button */}
            <Button 
              onClick={() => setShowAddMemberDialog(true)}
              className="bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Üzv Əlavə Et
            </Button>

            {/* Members List */}
            {groupMembers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">Bu qrupda heç kim yoxdur</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {groupMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="text-amber-700 font-semibold">
                          {member.full_name?.[0] || member.username[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {member.full_name || member.username}
                        </p>
                        <p className="text-sm text-gray-500">@{member.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                        {getRoleLabel(member.role)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersDialog(false)}>
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Qrupa Üzv Əlavə Et</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>İstifadəçi Seç</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="İstifadəçi seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableUsers().map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.username} ({getRoleLabel(u.role)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddMemberDialog(false)}>
                Ləğv et
              </Button>
              <Button 
                onClick={handleAddMember} 
                disabled={!selectedUserId}
                className="bg-green-600 hover:bg-green-700"
              >
                Əlavə Et
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Management Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              {selectedGroup?.name} - Admin Təyinatı
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Assign Admin Section */}
            <div className="flex gap-2">
              <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Admin seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableAdmins().map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.full_name || admin.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleAssignAdmin}
                disabled={!selectedAdminId}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Təyin Et
              </Button>
            </div>

            {/* Current Admins List */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Qrup Adminləri</Label>
              {groupAdmins.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <Shield className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm">Bu qrupa admin təyin edilməyib</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {groupAdmins.map((admin) => (
                    <div key={admin.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                          <Crown className="w-5 h-5 text-purple-700" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{admin.full_name || admin.username}</p>
                          <p className="text-sm text-gray-500">@{admin.username}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAdmin(admin.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p className="text-xs text-gray-500 bg-amber-50 p-2 rounded">
              💡 Qrup adminləri yalnız öz qruplarındakı istifadəçiləri görə və idarə edə bilər.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminDialog(false)}>
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
